<template>
    <div class="card border-0 rounded-0 bg-white lead-info-box">
        <div class="card-body p-15 p-sm-20 p-sm-25 pt-lg-30 pb-lg-30 ps-lg-25 pe-lg-25">
            <p v-if="survey.description && show_description">{{ survey.description }}</p>
            <div class="align-items-center d-flex flex-wrap gap-3 mb-10">
                <nav class="survey-ranges align-items-center d-flex flex-grow-1 gap-3 justify-content-end">
                    <button type="button" @click="prev"><i class="ph ph-caret-left"></i></button>
                    <div class="fw-bold text-black text-center flex-grow-1">{{ range_select_header }}</div>
                    <button type="button" @click="next"><i class="ph ph-caret-right"></i></button>
                </nav>
                <div class="form-group ms-auto">
                    <select
                        class="form-select shadow-none text-black p-10 pe-50 bg-body-tertiary fs-14 border-0 w-auto ms-auto"
                        :disabled="loading" required @change="updateRange" :value="range_type">
                        <option value="month">{{ $t("feature.therapy.$trend_range.$frequency.month") }}</option>
                        <option value="year">{{ $t("feature.therapy.$trend_range.$frequency.year") }}</option>
                    </select>
                </div>
            </div>
            <SpinnerLoading class="h-auto" v-if="responses === undefined" />
            <template v-else>
                <template v-if="responses?.length === 0">
                    <p class="text-paragraph mb-0">
                        {{ $t('feature.surveys.no_surveys') }}
                    </p>
                </template>
                <template v-else>
                    <template v-if="env === 'sifi_fk' || env === 'fungalk'">
                        <SurveyTable :survey="survey" :responses="responses" v-if="survey.key === 'gold-standard'" />
                        <SurveyResponses :survey="survey" :responses="responses" v-else />
                        <!-- LOADING -->
                        <div class="d-flex justify-content-center mt-15" v-if="loading">
                            <SpinnerLoading class="h-auto" />
                        </div>
                    </template>
                    <template v-else-if="env === 'kareena'">
                        <!-- CHART -->
                        <SurveyStatsChartWithRole :survey="survey" :responses="responses" />
                        <!-- DATAS -->
                        <div v-if="responses !== undefined && responses.length !== 0">
                            <!-- LOADING -->
                            <div class="d-flex justify-content-center mt-15" v-if="loading">
                                <SpinnerLoading class="h-auto" />
                            </div>
                            <!-- SHOW DATA -->
                            <div class="d-flex justify-content-between align-items-end flex-wrap gap-3" v-else>
                                <div class="mt-10">
                                    <button type="button" v-if="show_detail === false" @click="showDetail"
                                        class="ms-auto card-link-btn border-0 bg-transparent p-0 text-decoration-none text-primary fw-medium position-relative d-inline-block">
                                        {{ $t("cta.view_data") }}
                                    </button>
                                    <button type="button" v-else @click="show_detail = false"
                                        class="ms-auto card-link-btn border-0 bg-transparent p-0 text-decoration-none text-primary fw-medium position-relative d-inline-block">
                                        {{ $t("cta.hide_data") }}
                                    </button>
                                </div>
                            </div>
                            <template v-if="show_detail == true">
                                <div class="d-flex justify-content-end align-items-center gap-3 mb-25 mt-15">
                                    <div class="form-group">
                                        <select
                                            class="form-select shadow-none text-black p-15 pe-50 bg-body-tertiary fs-14 border-0"
                                            :disabled="loading" required v-model="data_typology">
                                            <option value="table">{{ $t("feature.surveys.typology.table") }}
                                            </option>
                                            <option value="list">{{ $t("feature.surveys.typology.list") }}</option>
                                        </select>
                                    </div>
                                </div>
                                <SurveyTable :survey="survey" :responses="responses" v-if="data_typology === 'table'" />
                                <SurveyResponses :survey="survey" :responses="responses"
                                    v-else-if="data_typology === 'list'" />
                            </template>
                        </div>
                    </template>
                </template>
            </template>
        </div>
    </div>
</template>
<style lang="scss">
.survey-ranges {
    & button {
        padding: 0;
        width: 35px;
        height: 35px;
        margin-left: 0;
        font-weight: 600;
        line-height: 34px;
        text-align: center;
        box-shadow: unset !important;
        transition: var(--transition);
        text-decoration: none;
        font-size: 14px;
        background-color: transparent;
        border-radius: 0.25em !important;
        color: var(--splash-muted-color);
        border: 1px solid rgba(101, 96, 240, 0.15);

        &:hover {
            background-color: var(--splash-primary-color);
            border-color: var(--splash-primary-color);
            color: var(--splash-white-color);
        }
    }

    & div {
        max-width: 150px;
    }
}
</style>
<script lang="ts">
import API from '@/services/api/api.service';
import { Survey, SurveyResponse } from '@/utils/models/survey.model';
import DateFormatUtils from "@/utils/methods/date_formats";
import { defineComponent, PropType } from 'vue';
import SpinnerLoading from "@/components/Widgets/Spinner.vue";
import SurveyResponses from './SurveyResponses.vue';
import SurveyStatsChart from './SurveyStatsChart.vue';
import { CLIENT, Environment } from '@/utils/const/env';
import SurveyEQ5D5L from './sifi/SurveyEQ5D5L.vue';
import SurveyTable from './SurveyTable.vue';
import dayjs from 'dayjs';
import toTitleCase from '@/utils/methods/string_extensions';
import { TherapiesStatisticsRangeType } from '@/utils/models/therapy.model';
import SurveyStatsChartWithRole from './SurveyStatsChartWithRole.vue';

const PAGE_SIZE = 10;

export type SurveyTypology = 'table' | 'list'
export default defineComponent({
    name: 'PatientSurveys',
    props: {
        survey: {
            type: Object as PropType<Survey>,
            required: true,
        },
        patient_id: {
            type: String,
            required: true,
        },
        show_description: {
            type: Boolean,
            default: true
        },
        typology: {
            type: Object as PropType<SurveyTypology>,
            default: 'table' as SurveyTypology
        }
    },
    data: function () {
        const DEFAULT_RANGE_TYPE: TherapiesStatisticsRangeType = 'year';
        return {
            responses: undefined,
            loading: false,
            show_detail: false,
            data_typology: this.$props.typology,
            range: {
                start: dayjs().startOf(DEFAULT_RANGE_TYPE).toDate(),
                end: dayjs().endOf(DEFAULT_RANGE_TYPE).toDate(),
            },
            range_type: DEFAULT_RANGE_TYPE as TherapiesStatisticsRangeType,
        } as {
            survey: Survey,
            responses?: SurveyResponse[],
            patient_id: string,
            loading: boolean,
            show_detail: boolean,
            data_typology: SurveyTypology,
            range: {
                start: Date,
                end: Date
            },
            range_type: TherapiesStatisticsRangeType,
        }
    },
    computed: {
        env(): Environment { return CLIENT },
        hide_header(): boolean {
            return this.survey.description === undefined || this.responses?.length === 0;
        },
        range_select_header() {
            return this.range_type === 'month' ? toTitleCase(DateFormatUtils.MONTH_YEAR(this.range.start)) : DateFormatUtils.YEAR(this.range.start);
        }
    },
    watch: {
        range: function (prev, next) {
            this.loading = true;
            this.responses = undefined;
            this.getSurveyAnswers();
        },
    },
    methods: {
        getSurveyAnswers() {
            API.getSurveyAnswers({ survey_id: this.survey.id, patient_id: this.patient_id, date_from: this.range.start, date_to: this.range.end, order: 'desc' }).then((response) => {
                // this.responses = [...(this.responses ?? []), ...(response.data.data ?? [])];
                this.responses = response.data.data ?? [];
                this.loading = false;
            }).catch((error) => {
                console.error(error);
                this.loading = false;
                this.responses = [...(this.responses ?? [])];
            });
        },
        formatTime(date: Date) {
            return DateFormatUtils.HHmm(date);
        },
        showDetail() {
            this.show_detail = true;
        },
        prev() {
            let ref = dayjs(this.range.start).subtract(1, 'day');
            this.range = {
                start: ref.startOf(this.range_type).toDate(),
                end: ref.endOf(this.range_type).toDate(),
            }
        },
        next() {
            let ref = dayjs(this.range.end).add(1, 'day');
            this.range = {
                start: ref.startOf(this.range_type).toDate(),
                end: ref.endOf(this.range_type).toDate(),
            }
        },
        updateRange(event) {
            this.range_type = event.target.value;
            this.range = {
                start: dayjs().startOf(this.range_type).toDate(),
                end: dayjs().endOf(this.range_type).toDate(),
            }
        }
    },
    mounted() {
        this.getSurveyAnswers();
    },
    components: {
        SpinnerLoading,
        SurveyStatsChartWithRole,
        SurveyResponses,
        // SurveyEQ5D5L,
        SurveyTable
    }
})

</script>
